import React from "react";
import { OverlayTrigger, Popover } from "react-bootstrap";
import { Link } from "react-router-dom";

import {
  Icon,
  KeyValueList,
  NoData,
  RenderWhen,
  StringWithCopy,
  Time
} from "@components";
import PreviewVisualizationModal from "@components/PreviewVisualizationModal";
import { usePopover } from "@js2/hooks";
import useModal from "@js2/hooks/useModal";
import useNetworkThreatIntelDomainReport from "@modules/network/hooks/useNetworkThreatIntelDomainReport";
import {
  useEmailPreview,
  useGetReportSummary,
  useLocalSampleSummaryParams
} from "@modules/samples/hooks";
import { getNtiUrl } from "../../../header/utils";
import { WarningIcon } from "../../../subheader/components/icons";
import { RECEIVED_HEADER_PATTERN } from "./consts";
import styles from "./email.module.scss";
import { EmailAuthResults } from "./EmailAuthResults";
import PreviewEmailButton from "./PreviewEmailButton";
import {
  getEmailAuthResults,
  getEmailDomain,
  getEmailHeader,
  getEmailUsername,
  getReceivedHeaders
} from "./utils";

interface LeftEmailColumnProps {
  metadataRoute: string;
}

const LeftEmailColumn: React.FC<LeftEmailColumnProps> = ({ metadataRoute }) => {
  const { hash, sourceId, relationshipId } = useLocalSampleSummaryParams();
  const [previewEmail, togglePreviewEmail] = useModal();
  const { data: report } = useGetReportSummary(hash, sourceId, relationshipId);
  const { emailPreviewThumbnail } = useEmailPreview(
    hash,
    report?.file_format ?? "",
    report?.source_sha1 ?? hash,
    relationshipId
  );

  const { email: { message = undefined } = {} } = report ?? {};
  // @ticore/metadata incorrectly sets the return type of email headers to be pair_t[],
  // however, a list of objects of type {name: string, value: string} is actually returned
  // we can remove this ts-ignore once this is fixed
  // @ts-ignore
  const returnPath = getEmailHeader(message?.headers, "Return-Path");
  // @ts-ignore
  const authResults = getEmailAuthResults(message?.headers);
  // @ts-ignore
  const receivedHeaders = getReceivedHeaders(message?.headers);
  const fullAuthHeader =
    // @ts-ignore
    getEmailHeader(message?.headers, "Authentication-Results") || "";
  const senders = message?.from;
  // Assuming there is always one sender - multiple "from" headers are allowed
  // but in that case there has to be one "sender" header
  const sender = senders?.at(0)?.email;
  const recipients = message?.recipients;
  const emailData = {
    sender: sender ? getEmailUsername(sender) : undefined,
    domain: sender ? getEmailDomain(sender) : undefined,
    receivers: recipients ?? undefined,
    auth_results: authResults,
    return_path: returnPath,
    date: message?.origination_date,
    messageID: message?.message_id
  };

  const { data: domainReport } = useNetworkThreatIntelDomainReport(
    emailData.domain
  );
  const domainReputation = domainReport?.third_party_reputations.statistics;
  const totalBadReputation =
    (domainReputation?.malicious ?? 0) + (domainReputation?.suspicious ?? 0);
  const [isShow, show, hide] = usePopover();

  return (
    <div className="flex justify-content-between width-50-pc">
      <KeyValueList className="flex flex-column justify-content-center flex-gap-2 width-75-pc overflow-hidden">
        <KeyValueList.Item
          label="Sender:"
          className="flex pdf-info-item"
          keyClassName="white-space-nowrap"
          value={
            emailData.sender && emailData.domain ? (
              <span>
                {`${emailData.sender}@`}
                <Link
                  to={getNtiUrl(emailData.domain ?? "")}
                  className={styles.item_link}
                >
                  {emailData.domain}
                </Link>
                {" ("}
                <span
                  style={{
                    display: "inline-flex",
                    verticalAlign: "middle",
                    gap: "2px"
                  }}
                >
                  {totalBadReputation ? (
                    <WarningIcon message="Suspicious or malicious third party reputation" />
                  ) : undefined}
                  {totalBadReputation}/{domainReputation?.total ?? <NoData />}
                </span>
                {")"}
              </span>
            ) : (
              <NoData />
            )
          }
        />
        <KeyValueList.Item
          label="Receiver(s):"
          className="flex pdf-info-item"
          keyClassName="white-space-nowrap"
          value={
            emailData.receivers
              ? emailData.receivers.map(recipient => recipient.email).join(", ")
              : undefined
          }
        />
        <KeyValueList.Item
          label="Received headers:"
          className="flex pdf-info-item"
          keyClassName="white-space-nowrap"
          value={
            <>
              <span
                style={{
                  display: "inline-flex",
                  verticalAlign: "middle",
                  gap: "2px"
                }}
              >
                {receivedHeaders?.length}{" "}
                <RenderWhen condition={!!receivedHeaders?.length}>
                  <OverlayTrigger
                    show={isShow}
                    delay={200}
                    overlay={
                      <Popover
                        onMouseEnter={show}
                        onMouseLeave={hide}
                        style={{ maxWidth: "50%" }}
                      >
                        <Popover.Body>
                          <ul className="popover-list">
                            {receivedHeaders
                              ?.map((header, idx) => (
                                <li key={idx}>
                                  {header.match(RECEIVED_HEADER_PATTERN)}
                                </li>
                              ))
                              .slice(0, 5)}
                          </ul>
                          <br />
                          <a href={metadataRoute}>See All Headers</a>
                        </Popover.Body>
                      </Popover>
                    }
                  >
                    <span
                      onMouseEnter={show}
                      onMouseLeave={hide}
                      style={{
                        display: "flex",
                        alignItems: "center"
                      }}
                      className={"pdf-hide-in-pdf"}
                    >
                      <Icon name="info-circle" />
                    </span>
                  </OverlayTrigger>
                </RenderWhen>
              </span>
            </>
          }
        />
        <KeyValueList.Item
          label="Return path:"
          className="flex pdf-info-item"
          keyClassName="white-space-nowrap"
          value={emailData.return_path}
        />
        <KeyValueList.Item
          label="Date:"
          className="flex pdf-info-item"
          keyClassName="white-space-nowrap"
          value={
            <RenderWhen
              condition={Boolean(emailData.date)}
              fallback={<NoData />}
            >
              <Time time={emailData.date} />
            </RenderWhen>
          }
          data-testid="email_date"
        />
        <KeyValueList.Item
          label="MessageID:"
          className="flex pdf-info-item"
          keyClassName="white-space-nowrap"
          value={<StringWithCopy value={emailData.messageID} />}
          data-testid="email_message_id"
        />
        <KeyValueList.Item
          label="DMARC / DKIM / SPF:"
          className="flex pdf-info-item"
          keyClassName="white-space-nowrap"
          value={
            <EmailAuthResults
              authResults={emailData.auth_results}
              fullAuthHeader={fullAuthHeader}
            />
          }
        />
      </KeyValueList>
      <PreviewEmailButton
        onClick={togglePreviewEmail}
        screenshotPath={emailPreviewThumbnail ?? ""}
      />
      <RenderWhen condition={previewEmail}>
        <PreviewVisualizationModal
          sha1={hash ?? ""}
          fileType={report?.sample_type ?? ""}
          fileSubtype={report?.sample_subtype ?? ""}
          typeDisplay={report?.file_format}
          sourceId={sourceId}
          threatStatus={report?.static_analysis?.classification ?? 0}
          relationshipId={relationshipId}
          sourceSampleSha1={report?.source_sha1 ?? hash}
          onHide={() => togglePreviewEmail()}
        />
      </RenderWhen>
    </div>
  );
};

export default LeftEmailColumn;
